import React, { useState } from 'react';
import {
  Calculator,
  Building2,
  User,
  BarChart3,
  CheckCircle,
  Settings,
  Clock,
  FileText,
  MessageSquare,
  ArrowRight
} from 'lucide-react';
import { Article, leaderArticles, employeeArticles, insightArticles } from '../generated/articles';

// Article Card Component - navigates to full article page
const ArticleCard: React.FC<{
  article: Article;
  colorClass: string;
  linkColorClass: string;
  onNavigateToArticle: (articleId: string) => void;
}> = ({ article, colorClass, linkColorClass, onNavigateToArticle }) => {
  const handleClick = () => {
    onNavigateToArticle(article.id);
  };

  return (
    <div
      id={article.id}
      onClick={handleClick}
      className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer group"
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {article.featured && (
              <span className={`inline-block px-3 py-1 text-xs font-semibold ${colorClass} rounded-full mb-3`}>
                Featured
              </span>
            )}
            <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition">{article.title}</h3>
            <p className="text-gray-600 mb-4">{article.subtitle}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{article.readTime}</span>
              <span className={`flex items-center text-sm font-medium ${linkColorClass} group-hover:translate-x-1 transition-transform`}>
                Read Article
                <ArrowRight className="w-4 h-4 ml-1" />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main ResourcesV2 Component
interface ResourcesV2Props {
  onNavigate?: (page: string) => void;
  onNavigateToArticle: (articleId: string) => void;
}

const ResourcesV2: React.FC<ResourcesV2Props> = ({ onNavigate, onNavigateToArticle }) => {
  const [activeSection, setActiveSection] = useState<string>('for-leaders');

  // Scroll to section with offset for sticky nav (nav height ~64px + category nav ~56px = 120px)
  const scrollToSection = (sectionId: string) => {
    setActiveSection(sectionId);
    const element = document.getElementById(sectionId);
    if (element) {
      const yOffset = -130; // Offset to account for sticky headers
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="pt-16 pb-20 bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Navigate AI Transformation<br className="hidden sm:block" /> With Your People
          </h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto">
            Practical insights, research, and tools for leaders and employees navigating the human side of AI adoption.
          </p>
        </div>
      </section>

      {/* Category Navigation - with visual separation from main nav */}
      <section className="bg-gray-50/80 backdrop-blur border-t border-gray-200 border-b shadow-sm sticky top-16 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex overflow-x-auto py-3 gap-2 sm:gap-4 sm:justify-center">
            <button
              onClick={() => scrollToSection('for-leaders')}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${activeSection === 'for-leaders' ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >For Leaders</button>
            <button
              onClick={() => scrollToSection('for-employees')}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${activeSection === 'for-employees' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >For Employees</button>
            <button
              onClick={() => scrollToSection('assessment-insights')}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${activeSection === 'assessment-insights' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >Assessment Insights</button>
            <button
              onClick={() => scrollToSection('in-practice')}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${activeSection === 'in-practice' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >In Practice</button>
            <button
              onClick={() => scrollToSection('tools')}
              className={`flex-shrink-0 px-4 py-2 rounded-full font-medium transition ${activeSection === 'tools' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >Tools</button>
          </div>
        </div>
      </section>

      {/* FOR LEADERS SECTION */}
      <section id="for-leaders" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center mr-4">
              <Building2 className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">For Leaders</h2>
              <p className="text-gray-600">Managing AI transformation with your people at the center</p>
            </div>
          </div>

          <div className="space-y-6 mt-8">
            {leaderArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                colorClass="text-teal-700 bg-teal-100"
                linkColorClass="text-teal-600"
                onNavigateToArticle={onNavigateToArticle}
              />
            ))}
          </div>
        </div>
      </section>

      {/* FOR EMPLOYEES SECTION */}
      <section id="for-employees" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mr-4">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">For Employees</h2>
              <p className="text-gray-600">Your agency, your career, your voice in AI transformation</p>
            </div>
          </div>

          <div className="space-y-6 mt-8">
            {employeeArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                colorClass="text-blue-700 bg-blue-100"
                linkColorClass="text-blue-600"
                onNavigateToArticle={onNavigateToArticle}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ASSESSMENT INSIGHTS SECTION */}
      <section id="assessment-insights" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mr-4">
              <BarChart3 className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Assessment Insights</h2>
              <p className="text-gray-600">What we're learning from real organizations</p>
            </div>
          </div>

          <div className="space-y-6 mt-8">
            {insightArticles.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                colorClass="text-purple-700 bg-purple-100"
                linkColorClass="text-purple-600"
                onNavigateToArticle={onNavigateToArticle}
              />
            ))}
          </div>
        </div>
      </section>

      {/* IN PRACTICE SECTION */}
      <section id="in-practice" className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mr-4">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">In Practice</h2>
              <p className="text-gray-600">Real stories of human-centered AI transformation</p>
            </div>
          </div>

          <div className="mt-8">
            <div className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Case Studies Coming Soon</h3>
              <p className="text-gray-600 max-w-md mx-auto">We're working with organizations right now to document their AI transformation journeys. Check back for real stories of what works—and what doesn't.</p>
              <p className="text-sm text-gray-500 mt-4">Want to be featured? <a href="mailto:mike@neupath.ai" className="text-teal-600 hover:text-teal-700">Let us know.</a></p>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS SECTION */}
      <section id="tools" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center mb-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mr-4">
              <Settings className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tools & Resources</h2>
              <p className="text-gray-600">Practical tools for your AI journey</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            {/* ROI Calculator */}
            <div
              className="bg-gradient-to-br from-teal-50 to-white rounded-xl p-6 shadow-lg border border-teal-100 hover:shadow-xl transition cursor-pointer"
              onClick={() => onNavigate?.('pricing')}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-teal-100 rounded-lg mr-4">
                  <Calculator className="w-6 h-6 text-teal-600" />
                </div>
                <span className="text-xs font-semibold text-teal-700 uppercase tracking-wide">Interactive Tool</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">ROI Calculator</h3>
              <p className="text-gray-600 text-sm mb-4">Estimate the return on investment for AI readiness assessment in your organization.</p>
              <span className="text-teal-600 font-semibold flex items-center text-sm">
                Try It Now
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>

            {/* I AM Assessment */}
            <div
              className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-6 shadow-lg border border-blue-100 hover:shadow-xl transition cursor-pointer"
              onClick={() => onNavigate?.('pricing')}
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Assessment</span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">I AM Assessment</h3>
              <p className="text-gray-600 text-sm mb-4">Discover your AI adoption mindset and understand how you naturally respond to AI transformation.</p>
              <span className="text-blue-600 font-semibold flex items-center text-sm">
                Learn More
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </span>
            </div>

            {/* Coming Soon: Templates */}
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-200 opacity-75">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-gray-200 rounded-lg mr-4">
                  <MessageSquare className="w-6 h-6 text-gray-500" />
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Template</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">AI Communication Templates</h3>
              <p className="text-gray-500 text-sm mb-4">Email and meeting templates for announcing AI initiatives to your team.</p>
              <span className="inline-block bg-gray-200 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">Coming Soon</span>
            </div>

            {/* Coming Soon: Checklist */}
            <div className="bg-gray-50 rounded-xl p-6 border-2 border-dashed border-gray-200 opacity-75">
              <div className="flex items-center mb-4">
                <div className="p-3 bg-gray-200 rounded-lg mr-4">
                  <FileText className="w-6 h-6 text-gray-500" />
                </div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Checklist</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Team Readiness Checklist</h3>
              <p className="text-gray-500 text-sm mb-4">A self-assessment checklist for leaders to evaluate their team's AI preparedness.</p>
              <span className="inline-block bg-gray-200 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">Coming Soon</span>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Understand Your Team's AI Readiness?
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            The I AM Assessment reveals who's ready to lead, who needs support, and how to move your entire organization forward together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => onNavigate?.('pricing')}
              className="bg-teal-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-400 transition transform hover:-translate-y-0.5"
            >
              Explore Assessment Options
            </button>
            <button
              onClick={() => onNavigate?.('contact')}
              className="bg-white/10 text-white px-8 py-4 rounded-full font-semibold border border-white/30 hover:bg-white/20 transition"
            >
              Talk to Us
            </button>
          </div>
        </div>
      </section>

      {/* Footer note */}
      <section className="py-8 bg-slate-900 text-center">
        <p className="text-gray-400 text-sm">This is a preview version for team feedback.</p>
      </section>
    </div>
  );
};

export default ResourcesV2;
